<script setup lang="ts">
import { ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { PieChart, Plus, Trash2, RefreshCw } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  'save': [code: string]
}>()

const showCode = ref(true)
const copied = ref(false)
const editableCode = ref('')

// Estado visual del Pie Chart
const chartTitle = ref('Mi Gráfico de Pastel')
const showData = ref(false)
const dataPoints = ref([
  { label: 'Categoría A', value: 40 },
  { label: 'Categoría B', value: 35 },
  { label: 'Categoría C', value: 25 },
])

const addDataPoint = () => {
  dataPoints.value.push({ label: `Nueva Categoría ${dataPoints.value.length + 1}`, value: 10 })
}

const removeDataPoint = (index: number) => {
  dataPoints.value.splice(index, 1)
}

// Generar código desde el estado visual
const generateMermaid = () => {
  let code = `pie${showData.value ? ' showData' : ''}\n`
  if (chartTitle.value.trim()) {
    code += `    title ${chartTitle.value.trim()}\n`
  }
  
  dataPoints.value.forEach(point => {
    const safeLabel = point.label.replace(/"/g, '') // Evitar comillas dobles que rompan la sintaxis
    code += `    "${safeLabel}" : ${point.value}\n`
  })
  
  editableCode.value = code
}

// Sincronizar desde código a estado visual
const syncToVisual = () => {
  const code = editableCode.value
  const lines = code.split('\n')
  
  const newPoints: { label: string, value: number }[] = []
  let newTitle = ''
  let newShowData = false
  
  for (const line of lines) {
    const tLine = line.trim()
    if (tLine.startsWith('pie')) {
      if (tLine.includes('showData')) {
        newShowData = true
      }
    } else if (tLine.startsWith('title ')) {
      newTitle = tLine.replace('title ', '').trim()
    } else if (tLine.match(/^"(.+)"\s*:\s*([\d.]+)/)) {
      const match = tLine.match(/^"(.+)"\s*:\s*([\d.]+)/)
      if (match) {
        newPoints.push({
          label: match[1],
          value: parseFloat(match[2])
        })
      }
    }
  }
  
  chartTitle.value = newTitle
  showData.value = newShowData
  if (newPoints.length > 0) {
    dataPoints.value = newPoints
  }
}

// Watchers
watch(() => props.open, (newVal) => {
  if (newVal) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (newVal && !editableCode.value) {
    generateMermaid()
  }
}, { immediate: true })

watch([chartTitle, showData, dataPoints], () => {
  generateMermaid()
}, { deep: true })

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const saveAndClose = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}
</script>

<template>
  <BaseVisualEditor
    :open="open"
    @update:open="$emit('update:open', $event)"
    title="Gráfico de Pastel (Pie Chart)"
    description="Agrega categorías y valores para generar un gráfico de distribución (pastel) de forma sencilla."
    :showCode="showCode"
    @update:showCode="showCode = $event"
    :copied="copied"
    @copy="copyToClipboard"
    @save="saveAndClose"
  >
    <template #icon>
      <PieChart class="w-5 h-5 text-primary" />
    </template>

    <template #toolbar>
      <Button size="sm" @click="addDataPoint"><Plus class="w-4 h-4 mr-2" /> Añadir Dato</Button>
    </template>

    <template #canvas>
      <div class="h-full w-full overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col gap-6">
        
        <!-- Configuración Principal -->
        <div class="bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4">
          <h3 class="font-semibold text-lg border-b pb-2">Configuración del Gráfico</h3>
          
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium">Título del Gráfico</label>
            <input 
              v-model="chartTitle" 
              class="border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Ej. Distribución de Presupuesto"
            />
          </div>
          
          <div class="flex items-center gap-2 mt-2">
            <input type="checkbox" id="showDataCheck" v-model="showData" class="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary" />
            <label for="showDataCheck" class="text-sm font-medium cursor-pointer">Mostrar valores numéricos en las etiquetas (showData)</label>
          </div>
        </div>

        <!-- Datos -->
        <div class="bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4">
          <div class="flex items-center justify-between border-b pb-2">
            <h3 class="font-semibold text-lg">Datos (Segmentos)</h3>
            <span class="text-xs text-muted-foreground">{{ dataPoints.length }} segmentos</span>
          </div>

          <div v-if="dataPoints.length === 0" class="text-center text-muted-foreground py-8 border-2 border-dashed rounded-md">
            No hay datos. Haz clic en "Añadir Dato".
          </div>

          <div class="flex flex-col gap-3">
            <div 
              v-for="(point, idx) in dataPoints" 
              :key="idx"
              class="flex items-center gap-3 bg-muted/50 p-2 rounded-md border border-transparent hover:border-border transition-colors group"
            >
              <div class="flex flex-col flex-1 gap-1">
                <label class="text-[10px] uppercase font-bold text-muted-foreground ml-1">Etiqueta (Nombre)</label>
                <input 
                  v-model="point.label" 
                  class="border-b bg-transparent px-2 py-1 text-sm focus:outline-none focus:border-primary font-medium"
                  placeholder="Ej. Manzanas"
                />
              </div>
              
              <div class="flex flex-col w-32 gap-1">
                <label class="text-[10px] uppercase font-bold text-muted-foreground ml-1">Valor</label>
                <input 
                  type="number"
                  v-model="point.value" 
                  class="border-b bg-transparent px-2 py-1 text-sm focus:outline-none focus:border-primary font-mono"
                  placeholder="0"
                />
              </div>

              <button 
                @click="removeDataPoint(idx)" 
                class="ml-2 w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:bg-destructive hover:text-white transition-colors"
                title="Eliminar dato"
              >
                <Trash2 class="w-4 h-4" />
              </button>
            </div>
          </div>
          
          <Button variant="outline" class="mt-2 border-dashed" @click="addDataPoint">
            <Plus class="w-4 h-4 mr-2" /> Añadir Nuevo Segmento
          </Button>
        </div>

      </div>
    </template>

    <template #code-panel>
      <div class="text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0">
        Mermaid (Sincronizado)
        <Button size="sm" variant="default" class="h-7 text-xs" @click="syncToVisual">
          <RefreshCw class="w-3 h-3 mr-2" />
          Sincronizar a Visual
        </Button>
      </div>
      <MermaidCodeEditor v-model="editableCode" class="flex-1 min-h-0" />
    </template>

    <template #live-preview>
      <MermaidLivePreview :code="editableCode" />
    </template>
  </BaseVisualEditor>
</template>
